import { powerSyncCollectionOptions } from "@tanstack/powersync-db-collection";
import { createCollection } from "@tanstack/react-db";
import {
  AccessMethodSchema,
  CheckInModeSchema,
  cancellationPolicyValues,
  PropertyOperationalStateSchema,
} from "@vidimo/shared";
import { z } from "zod";
import { AppSchema } from "@/lib/powersync-schema.generated";
import { collectionBase } from "./_factory";
import { syncedRow } from "./synced-row";

/**
 * Property: the BetterAuth tenant. BetterAuth-required fields (logo,
 * metadata, createdAt) and additionalFields (description, address, phone,
 * website + code, latitude, longitude, image, timezone) all live here.
 */
export const SelectPropertySchema = syncedRow({
  /** Guest-arrival details, edited in the "Arrival & Rules" settings section
   *  and rendered by the guest portal. `accessMethod` is NOT NULL in Postgres
   *  (default door_code); the rest are nullable free text — the portal
   *  degrades gracefully when a host hasn't filled them in.
   *
   *  `.catch`: rows synced before the sync-rule deploy carry no
   *  `accessMethod` (reads as null until the bucket reprocess re-delivers
   *  them); fall back to the DB default instead of failing every property
   *  row through that window. null is that expected window; any other
   *  unparseable value is corrupt data worth a trace before it's masked. */
  accessMethod: AccessMethodSchema.catch((ctx) => {
    if (ctx.value !== null) {
      console.warn(
        "[properties] unexpected access_method, falling back to door_code",
        ctx.value
      );
    }
    return "door_code" as const;
  }),
  address: z.string(),
  /** Finer Google subdivisions nothing reads; only older bundles write them. */
  adminAreaLevel2: z.string().nullable(),
  adminAreaLevel3: z.string().nullable(),
  /** What a guest booking on the public page is promised, and what the refund
   *  path honours. NOT NULL in Postgres (default flexible).
   *
   *  `.catch` for the same reason as `accessMethod` above: rows synced before
   *  this column reached the sync rules read as null until the bucket
   *  reprocess re-delivers them, and one such row must not fail every property
   *  in the app. Falls back to the DB default — the safest wrong answer, since
   *  it promises the guest MORE than a stricter policy would. */
  cancellationPolicy: z.enum(cancellationPolicyValues).catch((ctx) => {
    if (ctx.value !== null) {
      console.warn(
        "[properties] unexpected cancellation_policy, falling back to flexible",
        ctx.value
      );
    }
    return "flexible" as const;
  }),
  /** Who checks guests in (ADR-0146). Null until a row downloaded before the
   *  column existed syncs again: unknown, never read as self, which would
   *  check in everyone linked at an in-person property. */
  checkInMode: CheckInModeSchema.nullable().catch((ctx) => {
    console.warn("[properties] unexpected check_in_mode", ctx.value);
    return null;
  }),
  /** Local "HH:mm". */
  checkInTime: z.string().nullable(),
  checkOutTime: z.string().nullable(),
  city: z.string().nullable(),
  code: z.string(),
  /** Hex color (#RRGGBB) used as the per-property accent across the app —
   *  chat row left edges, calendar bar chips, filter dots. Required but
   *  nullable: PowerSync's bootstrap stream emits `color: null` for rows
   *  the admin hasn't customized yet, and `colorForProperty()` falls back
   *  to a deterministic palette pick from the property id in that case. */
  color: z.string().nullable(),
  /** ISO-3166-1 alpha-2, uppercase. */
  countryCode: z.string().nullable(),
  createdAt: z.string(),
  /** Property default currency (ISO-4217), seeded from countryCode at
   *  onboarding and host-editable. Nullable for properties created before
   *  this column existed; consumers fall back to the default currency. */
  currencyCode: z.string().nullable(),
  description: z.string(),
  /** Short stay notes (heating, bins, quiet hours). */
  houseInfo: z.string().nullable(),
  /** The rules guests agree to at check-in; shown in the guest portal. */
  houseRules: z.string().nullable(),
  id: z.string(),
  image: z.string().nullable(),
  latitude: z.number().nullable(),
  /** Short host recommendations. */
  localTips: z.string().nullable(),
  logo: z.string().nullable(),
  longitude: z.number().nullable(),
  metadata: z.string().nullable(),
  /** The country of the municipality the host picked; null until picked. */
  municipalityCountry: z.string().nullable(),
  name: z.string(),
  /** ADR-0144. `.catch` as for `accessMethod`: a row synced before this
   *  column reached the sync rules reads as null, and every property that
   *  existed then is active. The server refuses operational writes either
   *  way; this only shapes what the app offers. */
  operationalState: PropertyOperationalStateSchema.catch((ctx) => {
    if (ctx.value !== null) {
      console.warn(
        "[properties] unexpected operational_state, falling back to active",
        ctx.value
      );
    }
    return "active" as const;
  }),
  phone: z.string(),
  /** Canonical location captured via the Google Places picker. `placeId` is
   *  Google's stable identifier; city/region/postalCode/countryCode are the
   *  structured components the tourist-tax engine joins to. All nullable —
   *  properties created before the picker (or skipped) have none. */
  placeId: z.string().nullable(),
  postalCode: z.string().nullable(),
  /** When reception started refreshing each room before every arrival, by
   *  the server's clock once the write echoes back; null while off. */
  preArrivalRefreshSince: z.string().nullable(),
  receptionCloseTime: z.string().nullable(),
  /** Desk hours, local "HH:mm" — meaningful for staffed reception. */
  receptionOpenTime: z.string().nullable(),
  region: z.string().nullable(),
  /** Short display abbreviation (e.g. "EVA"). Surfaced on tight surfaces
   *  where the full property name doesn't fit. Nullable — consumers fall
   *  back to name-initials via `propertyShortLabel()`. */
  shortCode: z.string().nullable(),
  slug: z.string(),
  sublocality: z.string().nullable(),
  timezone: z.string().nullable(),
  updatedAt: z.string(),
  website: z.string(),
  wifiPassword: z.string().nullable(),
  wifiSsid: z.string().nullable(),
});

export type Property = z.infer<typeof SelectPropertySchema>;

export const propertiesCollection = createCollection(
  powerSyncCollectionOptions({
    ...collectionBase("property"),
    schema: SelectPropertySchema,
    table: AppSchema.props.properties,
  })
);
